import { Button } from '~/components/ui/Button';
import { WidgetText, WidgetTitle, WidgetWrapper } from '@agentselly/ui';
import { AppLink } from '~/components/AppLink';
import { useTranslate } from '@tolgee/react';
import { Gauge, Mail } from 'lucide-react';
import { AppRoute } from '@agentselly/immodossier-shared';
import { useAppParams } from '~/helper/useAppParams';

interface Props {
  readonly isFemale: boolean | undefined;
  readonly lastName: string;
  readonly link: string;
  readonly welcomeMessageGroup?: 'cockpit' | 'buyer' | 'default';
  readonly isAdvisor?: boolean;
  readonly customerName?: string;
  readonly dossierCreatedAt?: string | null;
  readonly cockpitPropertyId?: string | null;
  readonly v3Redesign?: boolean;
}

export function WelcomeWidget(props: Props) {
  const { t } = useTranslate();
  const params = useAppParams();
  const cockpitRoute =
    props.isAdvisor && props.cockpitPropertyId
      ? AppRoute.$LANG_$COMPANYID_$CONTACTID_COCKPIT_$PROPERTYID_HOME
          .replace('$lang', params.lang ?? '')
          .replace('$companyId', params.companyId ?? '')
          .replace('$contactId', params.contactId ?? '')
          .replace('$propertyId', props.cockpitPropertyId || '')
      : null;
  const createdAt = props.dossierCreatedAt
    ? new Date(props.dossierCreatedAt).toLocaleDateString('de-CH')
    : null;
  const translationSuffix =
    !props.welcomeMessageGroup || props.welcomeMessageGroup === 'default'
      ? ''
      : `_${props.welcomeMessageGroup}`;

  return (
    <WidgetWrapper>
      <div className="h-full flex flex-col gap-y-4">
        <WidgetTitle className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
          {props.lastName ? (
            <>
              {t('welcome_greetings')}{' '}
              {props.isFemale ? t('salutation_mrs') : t('salutation_mr')}{' '}
              {props.lastName}
            </>
          ) : (
            t('welcome_greetings')
          )}
        </WidgetTitle>

        {props.v3Redesign ? (
          <p
            className="text-sm text-gray-600 dark:text-zinc-400 break-words"
            data-testid="dossier-role-header"
          >
            {props.isAdvisor ? (
              <>
                {t('dossier_header_customer', 'Kunde')}:{' '}
                <b>{props.customerName || '–'}</b>
              </>
            ) : (
              <>
                {t('dossier_header_owner', 'Ihr Dossier')}
                {createdAt
                  ? ` · ${t(
                      'dossier_header_created',
                      'erstellt am',
                    )} ${createdAt}`
                  : ''}
              </>
            )}
          </p>
        ) : null}

        <WidgetText
          className={
            props.v3Redesign
              ? 'text-gray-600 dark:text-zinc-400'
              : 'text-gray-600 dark:text-gray-400'
          }
        >
          {t(`welcome_greetingtext${translationSuffix}`)}
        </WidgetText>

        {props.v3Redesign ? (
          <div className="flex flex-wrap gap-2">
            {cockpitRoute ? (
              <AppLink
                to={cockpitRoute}
                data-testid="link-cockpit"
                className="inline-flex items-center gap-2 rounded-lg border border-gray-200 dark:border-zinc-800 px-3 py-2 text-sm font-medium text-gray-900 dark:text-zinc-200 hover:bg-gray-50 dark:hover:bg-zinc-800"
              >
                <Gauge className="h-4 w-4" />
                {t('dossier_link_cockpit', 'Zum ImmoCockpit dieses Objekts')}
              </AppLink>
            ) : null}
            {props.isAdvisor ? (
              <a
                // ponytail: mailto share stub; add a real share flow (token link) when the backend supports it.
                href={`mailto:?subject=${encodeURIComponent(
                  t('dossier_share_subject', 'Ihr Dossier'),
                )}&body=${encodeURIComponent(window.location.href)}`}
                data-testid="share-dossier"
                className="inline-flex items-center gap-2 rounded-lg border border-gray-200 dark:border-zinc-800 px-3 py-2 text-sm font-medium text-gray-900 dark:text-zinc-200 hover:bg-gray-50 dark:hover:bg-zinc-800"
              >
                <Mail className="h-4 w-4" />
                {t('dossier_share', 'Dossier teilen')}
              </a>
            ) : null}
          </div>
        ) : null}

        {props.welcomeMessageGroup === 'buyer' ? (
          <a
            href={'https://www.agentselly.ch/suchprofil/'}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block"
            data-track="immodossier valiant welcome suchprofil link"
          >
            <Button
              primary
              showArrow
            >
              {t(`welcome_button_text${translationSuffix}`)}
            </Button>
          </a>
        ) : (
          <AppLink
            to={props.link}
            className="self-start"
            data-track="immodossier valiant welcome detail link"
          >
            <Button
              primary
              showArrow
            >
              {t(`welcome_button_text${translationSuffix}`)}
            </Button>
          </AppLink>
        )}
      </div>
    </WidgetWrapper>
  );
}
